'use client';

import { useEffect } from 'react';
import type { CmsData } from '../../../interfaces/webapp_json';
import { previewContentStore } from './previewContentStore';

/**
 * Bridges server-fetched CMS content into the client-side previewContentStore so that
 * preview bar controls (RevisionControl, LanguageControl) can access it from the root layout.
 *
 * A React Context Provider won't work here because the preview controls live in a separate
 * component tree (the layout) and context only flows downward within its own tree. useSWR
 * won't work either — the data is already fetched server-side, re-fetching it on the client
 * would be a redundant round-trip, and the layout controls don't know the page's CMS endpoint.
 * A module-level store is the correct bridge across RSC subtree boundaries in Next.js App Router.
 */
let generation = 0;

export const PreviewContentSync = ({ content }: { content: CmsData | null }) => {
  useEffect(() => {
    generation += 1;
    const id = generation;
    previewContentStore.set(content);
    return () => {
      // Only clear if no newer mount has already claimed ownership, i.e. we are truly
      // navigating to a page without CMS content rather than just transitioning between pages.
      if (id === generation) previewContentStore.set(null);
    };
  }, [content]);
  return null;
};
